import {
  VALID_TYPE_SET,
  clampName,
  hostFromUrl,
  parseCsvToObjects,
  passwordData,
  str,
  truthyFlag,
  withFolder,
} from './portabilityBasics';

/* ───────────────────────────── LastPass CSV ─────────────────────────────── */
// columns: url,username,password,totp,extra,name,grouping,fav

export function parseLastpassCsv(text) {
  const { records } = parseCsvToObjects(text);
  const items = [];
  let skipped = 0;
  for (const r of records) {
    const url = str(r.url);
    const favorite = truthyFlag(r.fav);
    const name = str(r.name);
    if (url.trim().toLowerCase() === 'http://sn') {
      const body = str(r.extra);
      if (name === '' && body === '') {
        skipped += 1;
        continue;
      }
      items.push(
        withFolder(
          { type: 'note', name: clampName(name, 'Secure note'), favorite, data: { body } },
          r.grouping
        )
      );
    } else {
      const hasContent = str(r.username) || str(r.password) || url.trim() || str(r.extra);
      if (name === '' && !hasContent) {
        skipped += 1;
        continue;
      }
      items.push(
        withFolder(
          {
            type: 'password',
            name: clampName(name, hostFromUrl(url) || 'Login'),
            favorite,
            data: passwordData({
              url,
              username: r.username,
              password: r.password,
              totp: r.totp,
              notes: r.extra,
            }),
          },
          r.grouping
        )
      );
    }
  }
  return { items, skipped };
}

/* ──────────────────────────── Apiboo JSON ───────────────────────────────── */

export function parseApibooJson(text) {
  const items = [];
  let skipped = 0;
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { items, skipped: 0 };
  }
  const list = Array.isArray(parsed && parsed.items)
    ? parsed.items
    : Array.isArray(parsed)
      ? parsed
      : [];
  for (const it of list) {
    if (
      !it ||
      typeof it !== 'object' ||
      !VALID_TYPE_SET.has(it.type) ||
      str(it.name).trim() === '' ||
      typeof it.data !== 'object' ||
      it.data == null
    ) {
      skipped += 1;
      continue;
    }
    items.push({
      type: it.type,
      name: clampName(it.name),
      favorite: !!it.favorite,
      data: it.data,
    });
  }
  return { items, skipped };
}

/* ──────────────────────────── Format detection ──────────────────────────── */

/**
 * Which import source does this text look like? Returns an IMPORT_SOURCES key
 * ('bitwardenJson' | 'apibooJson' | 'bitwardenCsv' | 'lastpassCsv') or null.
 *
 * WHY THIS EXISTS. The import dialog makes the user pick the source format,
 * and two of the four are ".csv" — so a LastPass export fed to the Bitwarden
 * CSV parser produced zero items with no explanation, which reads as "import
 * is broken" and was reported as exactly that. Content answers the question
 * better than a dropdown: the two CSV headers share no distinguishing columns,
 * and the two JSON shapes disagree about what `type` even is (Bitwarden: a
 * NUMBER; ours: a string like 'password').
 *
 * Detection is advisory — the caller re-parses with the detected source and
 * only believes it if items actually come out. A null just means "trust the
 * dropdown".
 */
export function detectVaultImportSource(text) {
  const raw = str(text);
  if (raw.trim() === '') return null;

  let parsed;
  try {
    parsed = JSON.parse(raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw);
  } catch {
    parsed = undefined;
  }
  if (parsed !== undefined) {
    const list = Array.isArray(parsed && parsed.items)
      ? parsed.items
      : Array.isArray(parsed)
        ? parsed
        : null;
    if (!list) return null;
    const first = list.find((e) => e && typeof e === 'object');
    if (!first) return null;
    // Bitwarden item types are integers (1=login, 2=note…); ours are strings.
    if (typeof first.type === 'number' || first.login || first.secureNote) return 'bitwardenJson';
    if (VALID_TYPE_SET.has(first.type) && typeof first.data === 'object') return 'apibooJson';
    return null;
  }

  const { header } = parseCsvToObjects(raw);
  if (header.length === 0) return null;
  const has = (name) => header.includes(name);
  // login_uri/login_username exist in no LastPass export; grouping+extra in no
  // Bitwarden one. Both checks demand a second witness column so a stray match
  // in somebody's unrelated spreadsheet does not misfire.
  if ((has('login_uri') || has('login_username')) && has('name')) return 'bitwardenCsv';
  if (has('url') && (has('grouping') || has('extra')) && has('name')) return 'lastpassCsv';
  return null;
}
